import axios from 'axios';
import io from 'socket.io-client';
import { 
  getOfflineData, 
  storeOfflineData, 
  queueOfflineRequest, 
  getOfflinePendingRequests,
  removeOfflineRequest
} from './offlineStorage';

const apiClient = axios.create({
  baseURL: import.meta.env.VITE_API_URL || 'https://api.poslah.com',
  timeout: 45000,
  headers: {
    'Content-Type': 'application/json',
  },
  withCredentials: true
});

// Request interceptor
apiClient.interceptors.request.use(
  config => {
    const token = localStorage.getItem('token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  error => Promise.reject(error)
);

// Enhanced response interceptor
apiClient.interceptors.response.use(
  response => response,
  async error => {
    // Handle 401 errors
    if (error.response?.status === 401) {
      localStorage.removeItem('token');
      localStorage.removeItem('userDetails');
      if (!window.location.pathname.includes('/signin')) {
        window.location.href = '/signin';
      }
    }
    return Promise.reject(error);
  }
);

// Enhance get requests with offline support
const originalGet = apiClient.get;
apiClient.get = async function(url, config) {
  try {
    const response = await originalGet(url, config);
    storeOfflineData(url, response.data);
    return response;
  } catch (error) {
    if (!navigator.onLine) {
      const cachedData = getOfflineData(url);
      if (cachedData) {
        return { 
          data: cachedData, 
          status: 200, 
          fromCache: true 
        };
      }
    }
    throw error;
  }
};

['post', 'put', 'delete'].forEach(method => {
  const original = apiClient[method];
  apiClient[method] = async function(url, data, config) {
    try {
      return await original(url, data, config);
    } catch (error) {
      if (!navigator.onLine) {
        queueOfflineRequest(url, method, data);
        return {
          data: { 
            success: true, 
            message: 'Queued for when online.',
            offlineQueued: true 
          },
          status: 202,
          offlineQueued: true
        };
      }
      throw error;
    }
  };
});

// Enhanced socket connection
const initializeSocket = () => {
  const token = localStorage.getItem('token');
  if (!token) return null;

  try {
    const backendUrl = import.meta.env.VITE_API_URL || 'https://api.poslah.com';
    
    const socket = io(backendUrl, {
      auth: { token },
      transports: ['websocket', 'polling'],
      reconnection: true,
      reconnectionDelay: 3000,
      reconnectionAttempts: 3,
      timeout: 30000
    });

    socket.on('connect', () => {
      console.log('🔌 Socket connected');
    });

    socket.on('connect_error', (error) => {
      console.error('❌ Socket error:', error.message);
    });

    return socket;
  } catch (error) {
    console.error('❌ Failed to initialize socket:', error);
    return null;
  }
};

// Only initialize socket when token exists and not on public pages
const shouldInitializeSocket = () => {
  const token = localStorage.getItem('token');
  const isPublicPage = ['/signin', '/signup', '/getstarted', '/'].includes(window.location.pathname);
  return token && !isPublicPage;
};

// Conditional socket initialization
if (shouldInitializeSocket()) {
  window.socket = initializeSocket();
} else {
  console.log('Socket disabled - no token or on public page');
  window.socket = null;
}

// Sync offline requests
export const syncOfflineRequests = async () => {
  if (!navigator.onLine) return;
  
  const pendingRequests = getOfflinePendingRequests();
  
  for (const request of pendingRequests) {
    try {
      await apiClient[request.method.toLowerCase()](
        request.endpoint, 
        request.data
      );
      removeOfflineRequest(request.id);
    } catch (error) {
      console.error(`Failed to process offline request ${request.id}:`, error);
    }
  }
};

if (typeof window !== 'undefined') {
  window.addEventListener('online', syncOfflineRequests);
}

export { initializeSocket };
export default apiClient;